﻿/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  Linking,
  Animated,
  ActivityIndicator,
  Share,
} from 'react-native';
import { Ionicons, FontAwesome5, MaterialIcons } from '@expo/vector-icons';


// JOBENG Logo component
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);

export default function CareerFairDetailsScreen({ navigation, route }) {
  // States
  const [isLoading, setIsLoading] = useState(true);
  const [eventDetails, setEventDetails] = useState(null);
  const [error, setError] = useState(null);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // ── Real fetch from /api/data/career-fairs/:id ────────────────────────────
  // Assumes table `career_fairs` with columns: id, title, organizer, location,
  // address, date, time, description, features[], attendees[], image, ...
  // If your table is named events / events_fairs / something else, swap below.



  // Load event details
  useEffect(() => {
    if (!route.params?.eventId) {
      setError('No event ID provided');
      setIsLoading(false);
      return;
    }

    const eventId = route.params.eventId;
    let mounted = true;

    (async () => {
      try {
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        const token = (await AsyncStorage.getItem('auth_token'))
                  || (await AsyncStorage.getItem('userToken'))
                  || (await AsyncStorage.getItem('token'))
                  || '';
        // Table: `events`, distinguished by event_type column.
        const res = await fetch(
          `https://api.jobeng.ai/api/data/events/${encodeURIComponent(eventId)}`,
          {
            headers: {
              'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const json = await res.json();
        if (!mounted) return;

        if (res.ok && (json?.success !== false)) {
          const raw = json.data || json.record || json;
          if (raw && raw.id != null) {
            // Map the real events-table shape to the props the rest of this
            // screen renders. Fields that don't exist in `events` default to
            // empty/null so the existing JSX renders gracefully.
            const fmtTime = (t) => {
              if (!t) return '';
              // PostgreSQL TIME returns 'HH:MM:SS' — strip the seconds.
              return typeof t === 'string' ? t.slice(0,5) : '';
            };
            const fmtDate = (d) => {
              if (!d) return '';
              try { return new Date(d).toISOString().split('T')[0]; } catch { return d; }
            };
            const time = (raw.start_time || raw.end_time)
              ? `${fmtTime(raw.start_time)}${raw.end_time ? ' - ' + fmtTime(raw.end_time) : ''}`
              : '';
            const event = {
              id:          raw.id,
              title:       raw.title || raw.name || 'Event',
              organizer:   raw.organizer || raw.host || raw.company_name || '',
              location:    raw.location || raw.venue || raw.city || '',
              address:     raw.address || [raw.city, raw.country].filter(Boolean).join(', '),
              date:        fmtDate(raw.start_date || raw.date || raw.event_date),
              time:        time,
              description: raw.description || '',
              features:    Array.isArray(raw.features) ? raw.features
                          : Array.isArray(raw.highlights) ? raw.highlights : [],
              attendees:   Array.isArray(raw.attendees) ? raw.attendees
                          : Array.isArray(raw.companies) ? raw.companies : [],
              image:       raw.image || raw.banner_url || raw.cover_image
                          || 'https://via.placeholder.com/400x200/0e4e96/FFFFFF?text=Event',
              eventType:   raw.event_type || '',
              ...raw, // keep all raw fields available too
            };
            setEventDetails(event);
          } else {
            setError('Career fair not found');
          }
        } else {
          setError(json?.error || 'Career fair not found');
        }
      } catch (e) {
        if (mounted) setError(e.message || 'Could not load career fair');
      } finally {
        if (mounted) {
          setIsLoading(false);

          // Animate content in
          Animated.parallel([
            Animated.timing(fadeAnim, {
              toValue: 1,
              duration: 500,
              useNativeDriver: true,
            }),
            Animated.timing(slideAnim, {
              toValue: 0,
              duration: 500,
              useNativeDriver: true,
            }),
          ]).start();
        }
      }
    })();

    return () => { mounted = false; };
  }, [route.params?.eventId, fadeAnim, slideAnim]);

  // Format date
  const formatDate = dateString => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return new Date(dateString).toLocaleDateString('en-GB', options);
  };

  // Calculate days until event
  const getDaysUntilEvent = eventDate => {
    const today = new Date();
    const date = new Date(eventDate);
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  // Handle share
  const handleShare = async () => {
    try {
      await Share.share({
        message: `Check out this career fair: ${eventDetails.title} on ${formatDate(eventDetails.date)}!`,
        url: eventDetails.registrationLink,
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Handle register
  const handleRegister = () => {
    // Navigate to registration screen, passing the correct prefixed ID
    navigation.navigate('CareerFairRegistration', {
      fairId: `fair${eventDetails.id}`,
    });
  };

  // Handle open external link
  const handleOpenLink = () => {
    if (eventDetails.registrationLink) {
      Linking.openURL(eventDetails.registrationLink);
    }
  };

  // Render loading state
  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color="#0e4e96" />
      <Text style={styles.loadingText}>Loading event details...</Text>
    </View>
  );

  // Render error state
  const renderError = () => (
    <View style={styles.errorContainer}>
      <Ionicons name="alert-circle-outline" size={60} color="#EF4444" />
      <Text style={styles.errorTitle}>Error</Text>
      <Text style={styles.errorMessage}>{error}</Text>
      <TouchableOpacity
        style={styles.errorButton}
        onPress={() => navigation.goBack()}
      >
        <Text style={styles.errorButtonText}>Go Back</Text>
      </TouchableOpacity>
    </View>
  );

  // Render event details
  const renderEventDetails = () => {
    const daysUntil = getDaysUntilEvent(eventDetails.date);
    const isPast = daysUntil < 0;
    const isSoon = daysUntil >= 0 && daysUntil <= 14;

    return (
      <Animated.ScrollView
        style={[styles.scrollView, { opacity: fadeAnim }]}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Event Image */}
        <Image
          source={{ uri: (eventDetails.image && typeof eventDetails.image=="string" && eventDetails.image.startsWith("http")) ? eventDetails.image : undefined }}
          style={styles.eventImage}
          defaultSource={require('../../../assets/default-event.png')}
        />

        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.eventTitle}>{eventDetails.title}</Text>
          <Text style={styles.eventOrganizer}>
            Organized by {eventDetails.organizer}
          </Text>
        </View>

        {/* Quick Info */}
        <View style={styles.quickInfoContainer}>
          <View style={styles.infoItem}>
            <Ionicons name="calendar-outline" size={20} color="#64748B" />
            <Text style={styles.infoText}>{formatDate(eventDetails.date)}</Text>
          </View>

          <View style={styles.infoItem}>
            <Ionicons name="time-outline" size={20} color="#64748B" />
            <Text style={styles.infoText}>{eventDetails.time}</Text>
          </View>

          <View style={styles.infoItem}>
            <Ionicons name="location-outline" size={20} color="#64748B" />
            <Text style={styles.infoText}>{eventDetails.location}</Text>
          </View>

          {eventDetails.address !== 'Virtual Event' && (
            <View style={styles.infoItem}>
              <Ionicons name="map-outline" size={20} color="#64748B" />
              <Text style={styles.infoText}>{eventDetails.address}</Text>
            </View>
          )}
        </View>

        {/* Badges */}
        <View style={styles.badgeContainer}>
          {eventDetails.isVirtual && (
            <View style={[styles.badge, styles.virtualBadge]}>
              <Ionicons name="globe-outline" size={14} color="#0ea5e9" />
              <Text style={styles.virtualBadgeText}>Virtual</Text>
            </View>
          )}
          <View style={[styles.badge, styles.categoryBadge]}>
            <Text style={styles.categoryBadgeText}>
              {eventDetails.category}
            </Text>
          </View>
          {eventDetails.isFree && (
            <View style={[styles.badge, styles.freeBadge]}>
              <Text style={styles.freeBadgeText}>Free Entry</Text>
            </View>
          )}
        </View>

        {/* Status */}
        <View style={styles.statusContainer}>
          {isPast ? (
            <Text style={styles.pastEventText}>Event has ended</Text>
          ) : isSoon ? (
            <Text style={styles.soonEventText}>
              {daysUntil === 0
                ? 'Today!'
                : daysUntil === 1
                  ? 'Tomorrow!'
                  : `${daysUntil} days away`}
            </Text>
          ) : (
            <Text style={styles.upcomingEventText}>Upcoming Event</Text>
          )}
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>About the Event</Text>
          <Text style={styles.descriptionText}>{eventDetails.description}</Text>
        </View>

        {/* Features */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Event Features</Text>
          {eventDetails.features.map((feature, index) => (
            <View key={index} style={styles.featureItem}>
              <Ionicons name="checkmark-circle" size={18} color="#0e4e96" />
              <Text style={styles.featureText}>{feature}</Text>
            </View>
          ))}
        </View>

        {/* Attendees */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Featured Employers</Text>
          <View style={styles.attendeesContainer}>
            {eventDetails.attendees.map((attendee, index) => (
              <View key={index} style={styles.attendeeChip}>
                <Text style={styles.attendeeText}>{attendee}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtonsContainer}>
          <TouchableOpacity style={styles.shareButton} onPress={handleShare}>
            <Ionicons name="share-social-outline" size={20} color="#0e4e96" />
            <Text style={styles.shareButtonText}>Share</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.registerButton, isPast && styles.disabledButton]}
            disabled={isPast}
            onPress={handleRegister}
          >
            <Text style={styles.registerButtonText}>
              {isPast ? 'Closed' : 'Register Now'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* External Registration Link */}
        <TouchableOpacity
          style={styles.externalLinkButton}
          onPress={handleOpenLink}
        >
          <Text style={styles.externalLinkText}>Visit Official Event Page</Text>
          <Ionicons name="open-outline" size={18} color="#0e4e96" />
        </TouchableOpacity>
      </Animated.ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.headerContainer}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Career Fair Details</Text>
        <JobengLogo />
      </View>

      {/* Content */}
      {isLoading
        ? renderLoading()
        : error
          ? renderError()
          : renderEventDetails()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#64748B',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E293B',
    marginTop: 16,
  },
  errorMessage: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 24,
  },
  errorButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  errorButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 20, // Add padding to bottom
  },
  eventImage: {
    width: '100%',
    height: 200,
    marginBottom: 16,
  },
  header: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  eventTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E293B',
    marginBottom: 4,
  },
  eventOrganizer: {
    fontSize: 16,
    color: '#64748B',
  },
  quickInfoContainer: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 15,
    color: '#334155',
    marginLeft: 8,
    flex: 1, // Allow text to wrap
  },
  badgeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 8,
  },
  virtualBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EFF6FF',
  },
  virtualBadgeText: {
    color: '#2563EB',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  categoryBadge: {
    backgroundColor: '#F0FDF4',
  },
  categoryBadgeText: {
    color: '#16A34A',
    fontSize: 12,
    fontWeight: '600',
  },
  freeBadge: {
    backgroundColor: '#FEF3C7',
  },
  freeBadgeText: {
    color: '#D97706',
    fontSize: 12,
    fontWeight: '600',
  },
  statusContainer: {
    paddingHorizontal: 16,
    marginBottom: 20,
  },
  pastEventText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
  },
  soonEventText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#DC2626',
  },
  upcomingEventText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#16A34A',
  },
  section: {
    paddingHorizontal: 16,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1E293B',
    marginBottom: 12,
  },
  descriptionText: {
    fontSize: 15,
    lineHeight: 22,
    color: '#334155',
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 15,
    color: '#334155',
    marginLeft: 8,
    flex: 1,
  },
  attendeesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  attendeeChip: {
    backgroundColor: '#E2E8F0',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 14,
    marginRight: 8,
    marginBottom: 8,
  },
  attendeeText: {
    fontSize: 13,
    color: '#475569',
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    marginTop: 8, // Add some margin above buttons
    marginBottom: 16,
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F5F9',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginRight: 12,
  },
  shareButtonText: {
    color: '#0e4e96',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  registerButton: {
    flex: 1,
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledButton: {
    backgroundColor: '#94A3B8',
  },
  registerButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  externalLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  externalLinkText: {
    color: '#0e4e96',
    fontSize: 16,
    fontWeight: '600',
    marginRight: 8,
  },
});
